import * as opt from "lib/opt"

export type Handler<T, R> = T extends undefined | null ? () => R : (data: T) => R

export type MatchHandlers<T extends Record<string, any>, R> =
	| { [K in keyof T]: Handler<T[K], R> }
	| (Partial<{ [K in keyof T]: Handler<T[K], R> }> & {
			_: Handler<T[keyof T], R>
	  }) /* eslint-disable-line */

// Helper type for array input
type arrToRecord<T extends readonly string[]> = {
	[K in T[int]]: undefined
}

// Define the return type for DefineEnum with object input
type objEnumResult<T extends Record<string, any>> = {
	Enum: Enum<T>
} & {
	[K in keyof T]: (data?: T[K]) => Variant<T, K>
}

// Define the return type for DefineEnum with array input
type arrayEnumResult<T extends readonly string[]> = {
	Enum: Enum<arrToRecord<T>>
} & {
	[K in T[int]]: () => Variant<arrToRecord<T>, K>
}

export type VariantOf<T> = T extends { Enum: infer E }
	? E extends { Members(): Array<infer V> } /* eslint-disable-line */
		? V
		: never
	: never

// Combined return type
export type EnumResult<T> = T extends readonly string[]
	? arrayEnumResult<T>
	: T extends Record<string, any>
		? objEnumResult<T>
		: never

export type IfLetCallbacks<E extends Record<string, any>> = {
	[K in keyof E]: <T>(callback: (data: E[K]) => T) => opt.Opt<NonNullable<T>>
}
// Improved Variant class that knows about all possible variants
export class Variant<E extends Record<string, any>, K extends keyof E = keyof E> {
	constructor(
		private readonly tag: K,
		private readonly data: E[K]
	) {}

	Is<S extends K>(value: S): bool {
		return this.tag === value
	}

	get Data(): E[K] {
		return this.data
	}

	get Tag(): K {
		return this.tag
	}

	IfLet(): IfLetCallbacks<E> {
		/* eslint-disable-next-line*/
		//@ts-ignore
		/* eslint-disable-next-line*/
		const self = this
		const handlers = {} as IfLetCallbacks<E>

		// Create a proxy to dynamically handle all possible variant keys
		return new Proxy(handlers, {
			get(target, prop) {
				return <T>(callback: (data: E[keyof E]) => T): opt.Opt<NonNullable<T>> => {
					if (
						self.tag === prop ||
						self.tag.String() === prop.toString() ||
						(typeof self.Tag === "string" &&
							typeof self.Tag.Equal === "function" &&
							self.Tag.Equal(prop.toString()))
					) {
						const result = callback(self.data as unknown as E[keyof E])
						return opt.Some(result as NonNullable<T>)
					}
					return opt.None()
				}
			},
		})
	}

	static New<
		T extends new (tag: K, data: E[K]) => any,
		E extends Record<string, any>,
		K extends keyof E,
	>(this: T, tag: K, data: E[K]): InstanceType<T> {
		/* eslint-disable-next-line */
		// @ts-ignore - TypeScript doesn't fully understand this pattern
		return new this(tag, data)
	}

	// Match method with improved type inference
	Match<R>(handlers: MatchHandlers<E, R>): R {
		const valueKey = this.tag

		if (valueKey in handlers && typeof handlers[valueKey] === "function") {
			// Check if data is null or undefined and call handler accordingly
			return isNil(this.data)
				? (handlers[valueKey] as any)()
				: (handlers[valueKey] as any)(this.data)
		}

		if ("_" in handlers && typeof handlers._ === "function") {
			// Same check for the fallback handler
			return this.data === null || this.data === undefined
				? (handlers._ as any)()
				: (handlers._ as any)(this.data)
		}

		log.Fatalf("Unhandled variant value: %s", this.tag.String())
	}
}

// The main Enum class
export class Enum<
	E extends Record<string, any>,
	V extends Variant<E, keyof E> = Variant<E, keyof E>,
> {
	private members: V[] = []
	private valueMap = new Map<string, V>()
	private typeName = ""

	constructor(members: V[]) {
		// Defensive check to ensure members is iterable
		if (!members) {
			this.members = []
			return
		}

		if (!Array.isArray(members)) {
			this.members = []
			return
		}

		try {
			this.members = [...members]

			// Build value map for fast lookups
			for (const member of this.members) {
				if (member && member.Tag) {
					this.valueMap.Set(member.Tag as string, member)
				} else {
					log.Warn("Invalid member in Enum constructor:", member)
				}
			}

			// Try to get the type name
			if (this.members.length > 0 && this.members[0]?.constructor) {
				this.typeName = this.members[0].constructor.name
			}
		} catch (error) {
			log.Warn("Error in Enum constructor:", error)
			this.members = []
		}
	}

	Contains<K extends keyof E>(memberOrKey: V | K): bool {
		if (memberOrKey instanceof Variant) {
			return this.valueMap.has(memberOrKey.Tag as string)
		} else {
			return this.valueMap.has(memberOrKey as string)
		}
	}

	Parse<K extends keyof E>(value: K): opt.Opt<V> {
		return opt.FromNullable(this.valueMap.get(value as string) as V)
	}

	Index(member: V): int {
		/* eslint-disable */
		//@ts-ignore
		const index = this.members.findIndex((m) => m.value === member.value)
		if (index === -1) {
			throw new Error(`Member with tag ${String(member.Tag)} not found in enum`)
		}
		return index
	}

	Len(): int {
		return this.members.length
	}

	static IsEnum<T extends Record<string, any>, V extends Variant<T, keyof T>>(
		value: unknown
	): value is Enum<T, V> {
		return !!(
			value &&
			typeof value === "object" &&
			"Members" in value &&
			typeof value.Members === "function"
		)
	}

	Empty(): bool {
		return this.members.length === 0
	}

	Members(): V[] {
		return [...this.members]
	}

	Values(): Array<keyof E> {
		/* eslint-disable */
		//@ts-ignore
		return this.members.map((m) => m.value)
	}

	Tag(member: V): keyof E {
		return member.Tag
	}

	TypeName(): string {
		return this.typeName
	}

	String(): string {
		/* eslint-disable */
		//@ts-ignore
		return this.members.map((m) => String(m.value)).join(", ")
	}

	Choice(seed = 0): V | null {
		if (this.members.length === 0) {
			return null
		}

		let randomValue: number
		if (seed !== 0) {
			// Simple deterministic random based on seed
			const s = Math.sin(seed) * 10000
			randomValue = s - Math.floor(s)
		} else {
			randomValue = Math.random()
		}

		const index = Math.floor(randomValue * this.members.length)
		return this.members[index]
	}
}

export function New<
	E extends Record<string, any>,
	V extends Variant<E, keyof E> = Variant<E, keyof E>,
>(members: V[]): Enum<E, V> {
	return new Enum<E, V>(members)
}

export function DefineEnum<T extends Record<string, any> | readonly string[]>(
	values: T
): EnumResult<T> {
	// Create results object
	const result = {} as any

	if (Array.isArray(values)) {
		// Convert array to record for type consistency
		const record = {} as Record<string, undefined>
		const variants: Variant<Record<string, undefined>>[] = []

		for (const value of values) {
			record[value] = undefined
			const variant = new Variant(value as string, undefined)
			variants.push!(variant)
			result[value] = () => new Variant(value as string, undefined)
		}

		result.Enum = new Enum<Record<string, undefined>>(variants)
	} else {
		// Handle object input
		const variants: Variant<Record<string, any>>[] = []

		for (const key in values) {
			if (Object.prototype.hasOwnProperty.call(values, key)) {
				const defaultData = values[key]
				const variant = new Variant(key, defaultData)
				variants.push!(variant)

				result[key] = (data = defaultData) =>
					new Variant(key, data !== undefined ? data : defaultData)
			}
		}

		result.Enum = new Enum<Record<string, any>>(variants)
	}

	return result as EnumResult<T>
}

// Example usage with enum that knows all variant types
// const ColorWithHex = DefineEnum({
// 	red: "#FF0000",
// 	green: { foo: "bar" },
// 	blue: 2,
// })
// //
// // // Now these should work with correct parameter types
// const redWithString = ColorWithHex.red("foo")

// redWithString.Match({
// 	red: (data) => {
// 		// data is string
// 	},
// 	green: (data) => {
// 		// data is { foo: string }
// 	},
//     _: () => {}
// })
// const blueWithNumber = ColorWithHex.green({ foo: "bar" });
//
// const test = DefineEnum(["red", "green", "blue"] as const);
// const p = test.red();
// p.Match({
// 	red: () => {
// 		// data is undefined
// 	},
// 	green: () => {
// 		// data is undefined
// 	},
// 	blue: () => {
// 		// data is undefined
// 	},
// 	// foo: (data) => {} // error
// });
//
// const s = test.Enum.Parse("red")
// 	.Unwrap()
// 	.Match({
// 		red: (data) => {
// 			// data is undefined
// 		},
// 		green: (data) => {
// 			// data is undefined
// 		},
// 		blue: (data) => {
// 			// data is undefined
// 		},
// 		// foo: (data) => {} // error
// 	});
//
// // Define a custom variant class with additional methods
//
//

// type BusinessObjMap = {
// 	C1: string;
// 	C2: int;
// };
// class BusinessObjVariant extends Variant<BusinessObjMap> {
// 	GetId(): string {
// 		return this.value;
// 	}
//
// 	// Add any other methods you need
// 	GetFormattedId(): string {
// 		return `ID-${this.value}`;
// 	}
// }
//
// const c1 = BusinessObjVariant.Create("C1", 2).Match({
//     C1: (data) => {
//     },
//     C2: (data) => {}
//     foo: (data) => {}
// })
//
